import type { ErrorCode, Locale } from '@qm/shared';
import { config } from '../config';

/** Ошибка API с машиночитаемым кодом; текст для пользователя берётся из локализации по коду. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    public readonly details?: { fields?: Record<string, string>; [k: string]: unknown },
    public readonly requestId?: string,
  ) {
    super(message);
  }
}

let token: string | null = null;
let onUnauthorized: (() => void) | null = null;
let onConsentRequired: (() => void) | null = null;
export const setConsentRequiredHandler = (fn: () => void) => {
  onConsentRequired = fn;
};

export const setToken = (t: string | null) => {
  token = t;
};
export const setUnauthorizedHandler = (fn: () => void) => {
  onUnauthorized = fn;
};

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, { ...init, headers, body });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Network error');
  }
  if (res.status === 204) return undefined as T;
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* пустое тело */
  }
  if (!res.ok) {
    const err = (data as { error?: { code?: ErrorCode; message?: string; details?: ApiError['details'] }; requestId?: string })
      ?.error;
    const apiErr = new ApiError(
      res.status,
      err?.code ?? 'INTERNAL_ERROR',
      err?.message ?? `HTTP ${res.status}`,
      err?.details,
      (data as { requestId?: string })?.requestId,
    );
    if (res.status === 401 && onUnauthorized && !path.startsWith('/auth/')) onUnauthorized();
    // Правила обновились во время сессии — показываем экран согласия.
    if (apiErr.code === 'CONSENT_REQUIRED' && onConsentRequired) onConsentRequired();
    throw apiErr;
  }
  return data as T;
}

/** Файл с авторизацией (голосовые сообщения доступны только участникам переписки). */
export async function apiBlob(path: string): Promise<Blob> {
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, { headers });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Network error');
  }
  if (!res.ok) throw new ApiError(res.status, res.status === 404 ? 'NOT_FOUND' : 'INTERNAL_ERROR', `HTTP ${res.status}`);
  return res.blob();
}

export const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

// ---------------- типы ответов API

export interface Price {
  type: 'fixed' | 'negotiable' | 'free' | 'exchange' | null;
  amount: string | null;
  formatted: string;
}

export interface ListingCardData {
  id: string;
  title: string;
  price: Price;
  condition: string | null;
  location: string | null;
  thumbUrl: string | null;
  categoryId?: string | null;
  imageCount: number;
  publishedAt: string | null;
  isVip: boolean;
  isHighlighted: boolean;
  isFavorite?: boolean;
  status?: string;
  available?: boolean;
  rejection?: { code: string | null; text: string | null } | null;
}

export interface ListingDetail {
  id: string;
  status?: string;
  title: string;
  description: string;
  price: Price;
  condition: string | null;
  category: { id: string; path: { id: string; name: string }[] } | null;
  categoryId: string | null;
  locationId: string | null;
  location: string | null;
  attributes: { key: string; label: string; value: string | number | boolean; display: string }[];
  images: { id: string; url: string; thumbUrl: string; width: number; height: number }[];
  publishedAt: string | null;
  updatedAt: string;
  expiresAt?: string | null;
  seller: { id: string; firstName: string; memberSince: string; activeListings: number; business: BusinessInfo | null; rating?: SellerRating | null; activity?: 'today' | 'week' | 'month' | null; avatarUrl?: string | null };
  isOwner: boolean;
  isFavorite: boolean;
  rejection: { code: string | null; text: string | null } | null;
  archiveReason?: string | null;
  viewCount?: number;
  shareUrl: string;
}

/** Свой бизнес-профиль (виден только владельцу), в т.ч. заявка на рассмотрении. */
export interface MyBusiness {
  name: string;
  description: string | null;
  status: 'pending' | 'active' | 'suspended';
  isVerified: boolean;
  activeUntil: string | null;
  /** Показывается ли покупателям сейчас (активен и не просрочен). */
  visible: boolean;
  requestedByUser: boolean;
}

export interface BusinessInfo {
  name: string;
  description?: string | null;
  isVerified: boolean;
  status: string;
}

export interface CategoryNode {
  id: string;
  parentId: string | null;
  slug: string;
  icon: string | null;
  name: string;
  hasCondition: boolean;
  children: CategoryNode[];
}

export interface LocationNode {
  id: string;
  parentId: string | null;
  type: string;
  slug: string;
  name: string;
  children: LocationNode[];
}

export interface AttributeView {
  id: string;
  key: string;
  type: 'text' | 'number' | 'select' | 'boolean';
  required: boolean;
  filterable: boolean;
  label: string;
  unit: string | null;
  min: number | null;
  max: number | null;
  options: { value: string; label: string }[] | null;
}

export interface Me {
  id: string;
  telegramId: string;
  username: string | null;
  firstName: string;
  status: 'active' | 'blocked' | 'deleted';
  blockedReason: string | null;
  memberSince: string;
  avatarUrl?: string | null;
  botStarted: boolean;
  settings: {
    locale: 'uz' | 'kaa' | 'ru';
    locationId: string | null;
    notifyListingStatus: boolean;
    notifyInquiries: boolean;
    onboardingCompleted: boolean;
  };
  contactPreferences: { showTelegramUsername: boolean; showPhone: boolean; phone: string | null; allowBotInquiries: boolean; allowChat?: boolean };
  deletionRequestedAt: string | null;
  consent: { requiredVersion: string; acceptedVersion: string | null; acceptedAt: string | null; needed: boolean };
  business: MyBusiness | null;
  /** Языки интерфейса, включённые администратором. */
  availableLocales: Locale[];
}

export interface ContactOptions {
  telegramUrl: string | null;
  phone: string | null;
  botInquiry: boolean;
  /** Можно написать в чат приложения. */
  chat?: boolean;
  available: boolean;
  isOwnListing: boolean;
}

// ---------------- чат

export type ChatBlocked = 'by_me' | 'by_them' | null;

export interface ChatListItem {
  id: string;
  role: 'buyer' | 'seller';
  listing: { id: string; title: string; status: string; thumbUrl: string | null; price: Price };
  counterpart: { name: string | null; deleted: boolean };
  lastMessage: { text: string; kind?: 'text' | 'voice'; mine: boolean; createdAt: string };
  unread: number;
  blocked: ChatBlocked;
}

export interface ChatHeader {
  id: string;
  role: 'buyer' | 'seller';
  listing: { id: string; title: string; status: string; thumbUrl: string | null; price: Price };
  counterpart: { id: string; name: string | null; deleted: boolean; blocked: boolean; activity?: 'today' | 'week' | 'month' | null; avatarUrl?: string | null };
  blocked: ChatBlocked;
  counterpartLastReadId: string;
  canSend: boolean;
}

export interface ChatMessage {
  id: string;
  mine: boolean;
  text: string;
  kind?: 'text' | 'voice';
  durationMs?: number;
  /** Своё только что записанное голосовое — играет из памяти, без загрузки. */
  localUrl?: string;
  createdAt: string;
  /** Локальное сообщение, ещё не подтверждённое сервером. */
  pending?: boolean;
  failed?: boolean;
}

export interface ChatMessagesPage {
  items: ChatMessage[];
  hasMore: boolean;
  counterpartLastReadId: string;
  blocked: ChatBlocked;
}

// ---------------- продвижение (Telegram Stars)

export interface PromotionTariffView {
  code: string;
  type: 'bump' | 'vip' | 'highlight';
  durationDays: number;
  stars: number;
  name: string;
}
export interface ListingPromotionState {
  vipUntil: string | null;
  highlightedUntil: string | null;
  lastBumpAt: string | null;
}

// ---------------- продавец и отзывы

export interface SellerRating {
  average: number;
  count: number;
}

export interface SellerProfile {
  id: string;
  firstName: string;
  avatarUrl?: string | null;
  memberSince: string;
  activeListings: number;
  soldListings: number;
  rating: (SellerRating & { positivePercent: number }) | null;
  /** Скорость ответа в чате (null — мало переписок для оценки). */
  response: { rate: number; speed: 'hour' | 'hours' | 'day' | 'days' | null } | null;
  business: BusinessInfo | null;
  activity?: 'today' | 'week' | 'month' | null;
}

export interface ReviewItem {
  id: string;
  rating: number;
  text: string | null;
  authorName: string | null;
  listingTitle: string | null;
  createdAt: string;
  edited: boolean;
}

export interface MyReviewState {
  eligible: boolean;
  reason: 'own' | 'no_chat' | null;
  review: { rating: number; text: string | null; hidden: boolean; createdAt: string; updatedAt: string } | null;
}

export interface Page<T> {
  items: T[];
  nextCursor?: string | null;
}
